/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AptosCoinDetailInfo } from '../models/AptosCoinDetailInfo';
import type { AptosCoinHolderListInfo } from '../models/AptosCoinHolderListInfo';
import type { AptosCoinListInfo } from '../models/AptosCoinListInfo';
import type { AptosCoinMarketCapListInfo } from '../models/AptosCoinMarketCapListInfo';
import type { AptosCoinNewListInfo } from '../models/AptosCoinNewListInfo';
import type { AptosCoinTopGainerListInfo } from '../models/AptosCoinTopGainerListInfo';
import type { AptosCoinTopLoserListInfo } from '../models/AptosCoinTopLoserListInfo';
import type { AptosCoinTransferListInfo } from '../models/AptosCoinTransferListInfo';
import type { AptosMemeCoinListInfo } from '../models/AptosMemeCoinListInfo';
import type { AptosTrxListInfo } from '../models/AptosTrxListInfo';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AptosCoinControllerService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @returns AptosCoinDetailInfo OK
     * @throws ApiError
     */
    public getAptosCoinDetail({
        coinId,
    }: {
        coinId: string,
    }): CancelablePromise<AptosCoinDetailInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin',
            query: {
                'coinId': coinId,
            },
        });
    }

    /**
     * @returns AptosCoinTransferListInfo OK
     * @throws ApiError
     */
    public getAptosCoinTransferList({
        coinId,
        stime,
        etime,
        from = '',
        to = '',
        page = 1,
        size = 25,
    }: {
        coinId: string,
        stime?: number,
        etime?: number,
        from?: string,
        to?: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosCoinTransferListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/transfer/list',
            query: {
                'coinId': coinId,
                'stime': stime,
                'etime': etime,
                'from': from,
                'to': to,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosTrxListInfo OK
     * @throws ApiError
     */
    public getAptosCoinTransactionList({
        coinId,
        page = 1,
        size = 25,
        stime,
        etime,
        from = '',
        to = '',
        fn = '',
        samt,
        eamt,
    }: {
        coinId: string,
        page?: number,
        size?: number,
        stime?: number,
        etime?: number,
        from?: string,
        to?: string,
        fn?: string,
        samt?: number,
        eamt?: number,
    }): CancelablePromise<AptosTrxListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/transaction/list',
            query: {
                'coinId': coinId,
                'page': page,
                'size': size,
                'stime': stime,
                'etime': etime,
                'from': from,
                'to': to,
                'fn': fn,
                'samt': samt,
                'eamt': eamt,
            },
        });
    }

    /**
     * @returns AptosCoinNewListInfo OK
     * @throws ApiError
     */
    public getNewCoin({
        type = 'ALL',
        size = 5,
    }: {
        type?: 'ALL' | 'MEME',
        size?: number,
    }): CancelablePromise<AptosCoinNewListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/new/list',
            query: {
                'type': type,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosMemeCoinListInfo OK
     * @throws ApiError
     */
    public getAptosMemeCoinList({
        sortField = 'HOLDER',
        sortDirection = 'DESC',
        type = 'ALL',
        page = 1,
        size = 25,
    }: {
        sortField?: 'PRICE' | 'AGE' | 'PRICE_CHANGE_5M' | 'PRICE_CHANGE_1H' | 'PRICE_CHANGE_6H' | 'PRICE_CHANGE_24H' | 'MARKET_CAP' | 'FDV' | 'HOLDER',
        sortDirection?: 'ASC' | 'DESC',
        type?: 'ALL' | 'COIN' | 'FUNGIBLE_ASSET',
        page?: number,
        size?: number,
    }): CancelablePromise<AptosMemeCoinListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/meme/list',
            query: {
                'sortField': sortField,
                'sortDirection': sortDirection,
                'type': type,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosCoinMarketCapListInfo OK
     * @throws ApiError
     */
    public getAptosCoinMarketCapList(): CancelablePromise<AptosCoinMarketCapListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/marketcap/list',
        });
    }

    /**
     * @returns AptosCoinTopLoserListInfo OK
     * @throws ApiError
     */
    public getTopLosers({
        type = 'ALL',
        size = 5,
    }: {
        type?: 'ALL' | 'MEME',
        size?: number,
    }): CancelablePromise<AptosCoinTopLoserListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/looser/list',
            query: {
                'type': type,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosCoinListInfo OK
     * @throws ApiError
     */
    public getAptosCoinList({
        sortField = 'HOLDER',
        sortDirection = 'DESC',
        type = 'ALL',
        page = 1,
        size = 25,
    }: {
        sortField?: 'NAME' | 'PRICE' | 'PRICE_CHANGE' | 'MARKET_CAP' | 'FDV' | 'VOLUME' | 'VOLUME_CHANGE' | 'HOLDER' | 'HOLDER_CHANGE',
        sortDirection?: 'ASC' | 'DESC',
        type?: 'ALL' | 'COIN' | 'FUNGIBLE_ASSET',
        page?: number,
        size?: number,
    }): CancelablePromise<AptosCoinListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/list',
            query: {
                'sortField': sortField,
                'sortDirection': sortDirection,
                'type': type,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosCoinHolderListInfo OK
     * @throws ApiError
     */
    public getAptosCoinHolderList({
        coinId,
        page = 1,
        size = 25,
    }: {
        coinId: string,
        page?: number,
        size?: number,
    }): CancelablePromise<AptosCoinHolderListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/holder/list',
            query: {
                'coinId': coinId,
                'page': page,
                'size': size,
            },
        });
    }

    /**
     * @returns AptosCoinTopGainerListInfo OK
     * @throws ApiError
     */
    public getTopGainer({
        type = 'ALL',
        size = 5,
    }: {
        type?: 'ALL' | 'MEME',
        size?: number,
    }): CancelablePromise<AptosCoinTopGainerListInfo> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/aptos/coin/gainer/list',
            query: {
                'type': type,
                'size': size,
            },
        });
    }

}
